<template>
  <SubViewWrapper
    title="Scheduled Column"
    :hide-header="embedded"
    @back="$emit('back')"
    @close="$emit('close')"
  >
    <div class="content" :class="{ embedded }">
      <div class="field-row">
        <span class="label">Column</span>
        <BaseInputSelect
          class="column-picker"
          select-only
          :options="columnOptions"
          :value="selectedValue"
          placeholder="Pick a date column…"
          :disabled="!columnOptions.length"
          @update:value="selectColumn"
        />
      </div>

      <div v-if="!embedded" class="actions">
        <BaseButton
          kind="main"
          type="primary"
          text="Save"
          :state="canSave ? 'default' : 'disabled'"
          @click="save"
        />
        <BaseButton
          v-if="scheduledMapping"
          kind="main"
          type="secondary"
          tint="red"
          text="Clear"
          @click="clear"
        />
      </div>
    </div>
  </SubViewWrapper>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref } from "vue";

import type {
  ColumnId,
  FieldWithOrigin,
  GraphDatabase,
} from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseInputSelect, {
  type SelectOption,
} from "@/components/base/BaseInputSelect.vue";
import SubViewWrapper from "@/components/views/config/SubViewWrapper.vue";

export default defineComponent({
  name: "ScheduledMappingConfig",
  components: { BaseButton, BaseInputSelect, SubViewWrapper },
  props: {
    scheduledMapping: {
      type: String as PropType<ColumnId>,
      required: false,
    },
    database: {
      type: Object as PropType<GraphDatabase>,
      required: true,
    },
    columnLabel: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * Rendered inside a host that owns the chrome (title bar and Save / Clear
     * actions, driven through `canSave`, `save` and `clear`).
     */
    embedded: { type: Boolean, default: false },
  },
  emits: {
    "update:scheduledMapping": (_: ColumnId | undefined) => true,
    back: () => true,
    close: () => true,
  },
  setup(props, { emit }) {
    const selectedColumnId = ref<ColumnId | null>(
      props.scheduledMapping ?? null
    );

    const isDateField = (field: FieldWithOrigin): boolean => {
      const v = field.value;
      if (v.type === "date") return true;
      if (v.type !== "variant") return false;
      return v.cases.some((c) => c.type === "date");
    };

    const dateColumns = computed<[ColumnId, FieldWithOrigin][]>(() =>
      Object.entries(props.database.type.fields).filter(([, field]) =>
        isDateField(field)
      )
    );

    const columnName = (id: ColumnId, fallback: string): string =>
      (id === props.scheduledMapping ? props.columnLabel : "") || fallback;
    // The mapped column may be missing from the sampled type (integration
    // columns without values yet): it stays selectable under the caller's
    // label, and reads as unset until that label is known.
    const columnOptions = computed<SelectOption[]>(() => {
      const options: SelectOption[] = dateColumns.value.map(([id, field]) => ({
        value: id,
        label: columnName(id, field.name),
        aliases: [],
      }));
      const mapped = props.scheduledMapping;
      if (
        mapped !== undefined &&
        props.columnLabel &&
        !options.some((o) => o.value === mapped)
      ) {
        options.push({ value: mapped, label: props.columnLabel, aliases: [] });
      }
      return options;
    });
    /** The picker's value: empty while the selected column is unknown. */
    const selectedValue = computed<string>(() =>
      columnOptions.value.some((o) => o.value === selectedColumnId.value)
        ? (selectedColumnId.value ?? "")
        : ""
    );

    const selectColumn = (id: ColumnId) => {
      selectedColumnId.value = id;
    };

    const canSave = computed<boolean>(() => selectedValue.value !== "");

    const save = () => {
      const col = selectedColumnId.value;
      if (!col) return;
      emit("update:scheduledMapping", col);
      emit("back");
    };

    const clear = () => {
      emit("update:scheduledMapping", undefined);
      emit("back");
    };

    return {
      columnOptions,
      selectedColumnId,
      selectedValue,
      selectColumn,
      canSave,
      save,
      clear,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss";

.content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;

  &.embedded {
    padding: 0;
  }
}

.field-row {
  display: flex;
  align-items: center;
  gap: 12px;

  .label {
    @extend %font-body-medium;
    flex-shrink: 0;
    width: 64px;
  }

  .column-picker {
    flex: 1;
  }
}

.actions {
  display: flex;
  gap: 8px;
  padding-top: 8px;
}
</style>
